import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { plainEnglishMessage } from "../../lib/plainEnglish";
import { CostConfirm } from "./CostConfirm";

const mmss = (s) => {
    const t = Math.round(Number(s) || 0);
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

export function ProjectStatusLine({ p }) {
    const parts = [
        `${p.scenesTotal} scenes`,
        `${p.approved} approved`,
        p.videoScenes != null ? `${p.videoScenesMeasured ? "" : "about "}${p.videoScenes} in the video` : null,
        p.durationS ? `${mmss(p.durationS)}${p.durationMeasured ? "" : " (estimated)"}` : null,
        p.images ? (p.images.used >= p.images.total ? "all images used" : `${p.images.used} of ${p.images.total} images used`) : null,
        `${p.buildsCompleted} finished build${p.buildsCompleted === 1 ? "" : "s"}`,
    ].filter(Boolean);
    return (
        <div data-testid="project-status-line" className="flex flex-wrap items-center gap-2 text-sm text-neutral-600">
            {p.running ? (
                <span data-testid="project-building" className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    <Loader2 size={12} className="animate-spin" /> Building your video…
                </span>
            ) : p.needsReview === 0 && p.scenesTotal > 0 ? (
                <CheckCircle2 size={14} className="text-emerald-600" />
            ) : null}
            <span>{parts.join(" · ")}</span>
            {p.settingsChanged && (
                <span data-testid="project-settings-changed" className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                    Settings changed: make the video again to apply them
                </span>
            )}
        </div>
    );
}

export function ProjectWarnings({ p, moduleId, onFixed }) {
    const [fixed, setFixed] = useState({});
    const [confirm, setConfirm] = useState(null);
    if (!p.warnings.length && !p.notes?.length) return null;
    const doFix = async (w, k) => {
        try {
            if (w.scene_id) await api.post(`/modules/${moduleId}/fix-visuals`, { scene_ids: [w.scene_id] });
            else await api.post(`/modules/${moduleId}/auto-fix`);
            setFixed((f) => ({ ...f, [k]: true }));
            toast.success("Fixed — it will be used on the next video build", { duration: 8000 });
            onFixed && onFixed();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const fix = (w, k) => setConfirm({
        title: w.scene_number ? `Fix scene ${w.scene_number}?` : "Fix this problem?",
        description: plainEnglishMessage(w), okLabel: "Fix it",
        estimate: { moduleId, action: "fix", sceneId: w.scene_id },
        onConfirm: () => doFix(w, k),
    });
    return (
        <div data-testid="project-warnings" className="space-y-1.5">
            {p.warnings.map((w, i) => {
                const k = `${w.scene_id || ""}-${i}`;
                return (
                    <div key={k} data-testid={`project-warning-${i}`} className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                        <div className="flex-1">
                            {w.scene_number && (
                                <Link data-testid={`project-warning-scene-link-${i}`} to={`/videos/${moduleId}?step=script&scene=${w.scene_id}`} className="mr-1 font-medium underline">
                                    Scene {w.scene_number}:
                                </Link>
                            )}
                            {plainEnglishMessage(w)}
                        </div>
                        {fixed[k] ? (
                            <span data-testid={`project-warning-fixed-${i}`} className="rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">Fixed</span>
                        ) : (
                            <button type="button" data-testid={`project-warning-fix-${i}`} onClick={() => fix(w, k)} className="rounded border border-amber-300 bg-white px-2 py-0.5 font-medium hover:bg-amber-100">
                                Fix it
                            </button>
                        )}
                    </div>
                );
            })}
            {(p.notes || []).map((w, i) => (
                <div key={`note-${w.scene_id || i}`} data-testid={`project-note-${i}`} className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                    <Info size={13} className="shrink-0 text-neutral-400" />
                    {w.scene_number && w.scene_id ? (
                        <Link data-testid={`project-note-scene-link-${i}`} to={`/videos/${moduleId}?step=script&scene=${w.scene_id}`} className="hover:underline">
                            Scene {w.scene_number} · motion graphic
                        </Link>
                    ) : <span>{w.scene_number ? `Scene ${w.scene_number} · motion graphic` : plainEnglishMessage(w)}</span>}
                </div>
            ))}
            <CostConfirm confirm={confirm} onClose={() => setConfirm(null)} />
        </div>
    );
}
